import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import DoctorLayout from "../../Layout/DoctorLayout";
import { getDoctorProfile } from "../../Features/Postes/Thunk/doctorExpertiseThunk";
import API from "../../Api/Api";
import ENDPOINTS from "../../Api/Endpoints";
import BidHistoryChat from "../../components/BidHistoryChat/BidHistoryChat";
import { tokenManager } from "../../Api/tokenManager";
import { SkeletonBlock } from "../../components/Skeleton/Skeleton";
import "./ConfirmedPatients.scss";

const PAGE_SIZE = 10;
const BIDS_PAGE_SIZE = 500;
const MAX_BID_API_PAGES = 100;
const TABLE_COLS = 6;

/** Returns true if this bid is "confirmed": doctor accepted patient price, or doctor countered and patient accepted */
function isConfirmedBid(item) {
  const pa = (item.providerAction || "").toLowerCase();
  const pta = (item.patientAction || "").toLowerCase();
  if (pa === "accepted_patient_price") return true;
  if (pa === "countered" && pta === "accepted") return true;
  return false;
}

function formatVisitCell(item) {
  const ap = item.appointment;
  if (ap?.appointmentDate) {
    const d = new Date(ap.appointmentDate);
    if (Number.isFinite(d.getTime())) {
      const dateStr = d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
      return ap.timeSlot ? `${dateStr} · ${ap.timeSlot}` : dateStr;
    }
  }
  const pr = item.matchRequest?.preferredDate;
  if (pr) {
    const d = new Date(pr);
    if (Number.isFinite(d.getTime())) {
      return `Preferred: ${d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}`;
    }
  }
  const confirmedAt = item.updatedAt || item.createdAt;
  if (confirmedAt) {
    return new Date(confirmedAt).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
  }
  return "—";
}

function rowSortTimeMs(row) {
  const ap = row.raw?.appointment?.appointmentDate;
  if (ap) {
    const t = new Date(ap).getTime();
    if (Number.isFinite(t)) return t;
  }
  const pr = row.raw?.matchRequest?.preferredDate;
  if (pr) {
    const t = new Date(pr).getTime();
    if (Number.isFinite(t)) return t;
  }
  const u = row.raw?.updatedAt || row.raw?.createdAt;
  return u ? new Date(u).getTime() : 0;
}

function startOfLocalDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

/** Visit list status filter: Upcoming | Today | Completed */
function deriveConfirmedPatientStatus(item) {
  const tc = item.treatmentCompletedAt;
  if (tc != null && tc !== "") {
    const t = new Date(tc).getTime();
    if (Number.isFinite(t)) return "Completed";
  }
  const ap = item.appointment;
  const apStatus = String(ap?.status || "").toLowerCase();
  if (apStatus === "completed" || apStatus === "cancelled") return "Completed";

  const apDateRaw = ap?.appointmentDate;
  if (apDateRaw) {
    const apDate = new Date(apDateRaw);
    if (Number.isFinite(apDate.getTime())) {
      const todayStart = startOfLocalDay(new Date());
      const dayStart = startOfLocalDay(apDate);
      if (dayStart === todayStart) return "Today";
      if (dayStart < todayStart) return "Completed";
      return "Upcoming";
    }
  }

  const pr = item.matchRequest?.preferredDate;
  if (pr) {
    const d = new Date(pr);
    if (Number.isFinite(d.getTime())) {
      const todayStart = startOfLocalDay(new Date());
      const dayStart = startOfLocalDay(d);
      if (dayStart === todayStart) return "Today";
      if (dayStart < todayStart) return "Completed";
      return "Upcoming";
    }
  }

  return "Upcoming";
}

/** Map API bid item to confirmed-patient row */
function mapBidToConfirmedRow(item) {
  const name = String(item.patient?.name || "").trim() || "Patient";
  const location = String(item.patient?.location || "").trim() || "—";
  const proc =
    String(item.procedureLabel || "").trim() ||
    [item.category?.name, item.subCategory?.name].filter(Boolean).join(" · ").trim() ||
    "—";
  const symptoms = String(item.matchRequest?.symptomsDescription || "").trim();
  const age =
    item.patient?.ageYears != null && Number.isFinite(Number(item.patient.ageYears))
      ? `${item.patient.ageYears} yrs`
      : "—";
  const blood = String(item.patient?.bloodGroup || "").trim() || "—";
  const docCount = Array.isArray(item.prescriptionsAndReports) ? item.prescriptionsAndReports.length : 0;
  return {
    id: item.bidId,
    bidId: item.bidId,
    patientId: item.patient?.id,
    name,
    legalName: String(item.patient?.legalName || "").trim(),
    avatar: item.patient?.avatar,
    location,
    procedure: proc,
    symptomsShort: symptoms.length > 100 ? `${symptoms.slice(0, 97)}…` : symptoms,
    symptomsFull: symptoms,
    ageLabel: age,
    bloodLabel: blood,
    gender: String(item.patient?.gender || "").trim(),
    appointmentDate: formatVisitCell(item),
    documentCount: docCount,
    status: deriveConfirmedPatientStatus(item),
    raw: item,
  };
}

export default function ConfirmedPatients() {
  const dispatch = useDispatch();
  const { doctorProfile } = useSelector((s) => s.doctorExpertise || {});

  const normalizedProfile = useMemo(() => {
    const p = doctorProfile || null;
    return p?.data?.data ?? p?.data ?? p?.doctor ?? p ?? {};
  }, [doctorProfile]);

  const chatMode = tokenManager.getActiveProviderRole() === "hospital" ? "hospital" : "doctor";
  const bidsListPath = chatMode === "hospital" ? ENDPOINTS.HOSPITAL_BIDS : ENDPOINTS.DOCTOR_BIDS;
  const docPreviewPath =
    chatMode === "hospital" ? ENDPOINTS.HOSPITAL_PATIENT_DOCUMENT_PREVIEW : ENDPOINTS.DOCTOR_PATIENT_DOCUMENT_PREVIEW;

  const [statusFilter, setStatusFilter] = useState("All");
  const [sortBy, setSortBy] = useState("Date ( Soonest )");
  const [page, setPage] = useState(1);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chatModal, setChatModal] = useState(null);
  const [detailsModal, setDetailsModal] = useState(null);
  const [docPreviewUrls, setDocPreviewUrls] = useState({});
  const [docPreviewLoading, setDocPreviewLoading] = useState(false);
  const [fileViewer, setFileViewer] = useState(null);
  const docBlobCleanupRef = useRef([]);

  useEffect(() => {
    dispatch(getDoctorProfile());
  }, [dispatch]);

  const fetchConfirmed = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const merged = [];
      const seenIds = new Set();
      for (let apiPage = 1; apiPage <= MAX_BID_API_PAGES; apiPage += 1) {
        const res = await API.get(bidsListPath, {
          params: { page: apiPage, limit: BIDS_PAGE_SIZE },
        });
        const body = res?.data;
        const chunk = Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : [];
        if (!chunk.length) break;
        for (const row of chunk) {
          const rawId = row?.bidId ?? row?._id;
          const key = rawId != null ? String(rawId) : `__idx_${merged.length}`;
          if (seenIds.has(key)) continue;
          seenIds.add(key);
          merged.push(row);
        }
        if (chunk.length < BIDS_PAGE_SIZE) break;
      }
      const confirmedOnly = merged.filter(isConfirmedBid).map(mapBidToConfirmedRow);
      setList(confirmedOnly);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to load confirmed patients");
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [bidsListPath]);

  useEffect(() => {
    const status = String(normalizedProfile?.status ?? "").toLowerCase();
    if (status === "approved") {
      fetchConfirmed();
    }
  }, [normalizedProfile?.status, fetchConfirmed]);

  const filtered = useMemo(() => {
    let result = list.filter((item) => {
      const matchesStatus =
        statusFilter === "All" ||
        (item.status && item.status.toLowerCase() === statusFilter.toLowerCase());
      return matchesStatus;
    });
    if (sortBy === "Date ( Soonest )") {
      result = result.slice().sort((a, b) => rowSortTimeMs(a) - rowSortTimeMs(b));
    } else if (sortBy === "Date ( Latest )") {
      result = result.slice().sort((a, b) => rowSortTimeMs(b) - rowSortTimeMs(a));
    } else if (sortBy === "Name ( A-Z )") {
      result = result.slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }
    return result;
  }, [list, statusFilter, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const rows = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page, totalPages]);

  useEffect(() => {
    if (page > totalPages) setPage((p) => Math.max(1, totalPages));
    if (page < 1) setPage(1);
  }, [page, totalPages]);

  useEffect(() => {
    docBlobCleanupRef.current.forEach((u) => {
      try {
        URL.revokeObjectURL(u);
      } catch (_) {}
    });
    docBlobCleanupRef.current = [];
    setDocPreviewUrls({});

    if (!detailsModal?.raw) {
      setDocPreviewLoading(false);
      return undefined;
    }

    const files = Array.isArray(detailsModal.raw.prescriptionsAndReports)
      ? detailsModal.raw.prescriptionsAndReports
      : [];
    const docs = [
      ...files.filter((f) => (f.type || "").toLowerCase() === "prescription"),
      ...files.filter((f) => (f.type || "").toLowerCase() === "report"),
      ...files.filter((f) => (f.type || "").toLowerCase() === "insurance"),
    ];

    if (docs.length === 0) {
      setDocPreviewLoading(false);
      return undefined;
    }

    let cancelled = false;
    setDocPreviewLoading(true);

    (async () => {
      const next = {};
      const created = [];
      await Promise.all(
        docs.map(async (doc) => {
          const id = doc?._id;
          if (!id) return;
          try {
            const res = await API.get(docPreviewPath(id), { responseType: "blob" });
            const blob = res?.data;
            if (blob instanceof Blob && !cancelled) {
              const obj = URL.createObjectURL(blob);
              created.push(obj);
              next[String(id)] = obj;
            }
          } catch (_) {}
        })
      );
      if (!cancelled) {
        docBlobCleanupRef.current = created;
        setDocPreviewUrls(next);
      } else {
        created.forEach((u) => {
          try {
            URL.revokeObjectURL(u);
          } catch (_) {}
        });
      }
      if (!cancelled) setDocPreviewLoading(false);
    })();

    return () => {
      cancelled = true;
      docBlobCleanupRef.current.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch (_) {}
      });
      docBlobCleanupRef.current = [];
    };
  }, [detailsModal, docPreviewPath]);

  const previewDocUrl = (doc) => {
    const id = doc?._id != null ? String(doc._id) : "";
    return id ? docPreviewUrls[id] : "";
  };

  const openFileViewer = (doc, fallbackTitle) => {
    const url = previewDocUrl(doc);
    if (!url) return;
    setFileViewer({
      url,
      title: doc.originalName || fallbackTitle || "File",
      fileType: (doc.fileType || "").toLowerCase(),
    });
  };

  const handleMessage = (patientRow) => {
    setChatModal({
      bidId: patientRow?.bidId,
      patientName: patientRow?.name,
      raw: patientRow?.raw,
    });
  };

  return (
    <DoctorLayout>
      <div className="confirmed_patient_page">
        <div className="confirmed_patient_controls">
          <div className="confirmed_patient_selects">
            <div className="confirmed_patient_select">
              <span className="confirmed_patient_select_label">Status :</span>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option>Upcoming</option>
                <option>Today</option>
                <option>Completed</option>
                <option>All</option>
              </select>
              <span className="confirmed_patient_caret">▾</span>
            </div>

            <div className="confirmed_patient_select">
              <span className="confirmed_patient_select_label">Sort :</span>
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPage(1);
                }}
              >
                <option>Date ( Soonest )</option>
                <option>Date ( Latest )</option>
                <option>Name ( A-Z )</option>
              </select>
              <span className="confirmed_patient_caret">▾</span>
            </div>
          </div>
        </div>

        <div className="confirmed_patient_table_wrap">
          <table className="confirmed_patient_table">
            <thead>
              <tr>
                <th className="confirmed_patient_th_name">Patient</th>
                <th className="confirmed_patient_th_proc">Procedure &amp; request</th>
                <th className="confirmed_patient_th_demo">Age / blood</th>
                <th>Location</th>
                <th>Visit</th>
                <th className="confirmed_patient_th_contact">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading &&
                Array.from({ length: PAGE_SIZE }).map((_, idx) => (
                  <tr key={`confirmed-skel-${idx}`}>
                    <td className="confirmed_patient_td_name">
                      <span className="confirmed_patient_avatar" />
                      <div className="confirmed_patient_name_stack">
                        <SkeletonBlock height={14} width="60%" />
                        <div style={{ height: 6 }} />
                        <SkeletonBlock height={10} width="40%" />
                      </div>
                    </td>
                    <td>
                      <SkeletonBlock height={14} width="85%" />
                      <div style={{ height: 6 }} />
                      <SkeletonBlock height={10} width="70%" />
                    </td>
                    <td>
                      <SkeletonBlock height={14} width="50%" />
                    </td>
                    <td className="confirmed_patient_muted">
                      <SkeletonBlock height={14} width="70%" />
                    </td>
                    <td>
                      <SkeletonBlock height={14} width="65%" />
                    </td>
                    <td className="confirmed_patient_td_contact">
                      <SkeletonBlock height={34} width={160} />
                    </td>
                  </tr>
                ))}
              {!loading && error && (
                <tr>
                  <td colSpan={TABLE_COLS} className="confirmed_patient_no_rows">
                    {error}
                  </td>
                </tr>
              )}
              {!loading &&
                !error &&
                rows.map((item) => (
                  <tr key={item.id}>
                    <td className="confirmed_patient_td_name">
                      {item.avatar ? (
                        <img src={item.avatar} alt="" className="confirmed_patient_avatar_img" />
                      ) : (
                        <span className="confirmed_patient_avatar" />
                      )}
                      <div className="confirmed_patient_name_stack">
                        <span className="confirmed_patient_name_text">{item.name}</span>
                        {item.legalName && item.legalName !== item.name ? (
                          <span className="confirmed_patient_name_legal">Profile: {item.legalName}</span>
                        ) : null}
                      </div>
                    </td>

                    <td className="confirmed_patient_td_proc">
                      <div className="confirmed_patient_proc_title">{item.procedure}</div>
                      {item.symptomsShort ? (
                        <div className="confirmed_patient_proc_sub">{item.symptomsShort}</div>
                      ) : (
                        <div className="confirmed_patient_proc_sub confirmed_patient_proc_sub--muted">No clinical note on file</div>
                      )}
                    </td>

                    <td className="confirmed_patient_td_demo">
                      <div>{item.ageLabel}</div>
                      <div className="confirmed_patient_blood">{item.bloodLabel}</div>
                      {item.gender ? <div className="confirmed_patient_gender">{item.gender}</div> : null}
                    </td>

                    <td className="confirmed_patient_muted">{item.location}</td>

                    <td className="confirmed_patient_muted confirmed_patient_td_visit">{item.appointmentDate}</td>

                    <td className="confirmed_patient_td_contact">
                      <div className="confirmed_patient_action_group">
                        <button
                          type="button"
                          className="confirmed_patient_details_btn"
                          onClick={() => setDetailsModal(item)}
                        >
                          Details
                          {item.documentCount > 0 ? (
                            <span className="confirmed_patient_doc_badge">{item.documentCount}</span>
                          ) : null}
                        </button>
                        <button type="button" className="confirmed_patient_msg_btn" onClick={() => handleMessage(item)}>
                          <span className="confirmed_patient_btn_icon">💬</span>
                          <span>Message</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

              {!loading && !error && !rows.length && (
                <tr>
                  <td colSpan={TABLE_COLS} className="confirmed_patient_no_rows">
                    No confirmed patients found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="confirmed_patient_table_footer">
            <div className="confirmed_patient_pager">
              <button
                type="button"
                className="confirmed_patient_pg_btn ghost"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Prev
              </button>

              {renderPages(page, totalPages, setPage)}

              <button
                type="button"
                className="confirmed_patient_pg_btn ghost"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>

            <div className="confirmed_patient_page_meta">
              <span className="confirmed_patient_muted">Page</span>

              <div className="confirmed_patient_page_drop">
                <select value={page} onChange={(e) => setPage(Number(e.target.value))}>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <option key={i} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </select>
                <span className="confirmed_patient_caret">▾</span>
              </div>

              <span className="confirmed_patient_muted">Of {totalPages}</span>
            </div>
          </div>
        </div>
      </div>

      {detailsModal ? (
        <div className="cp_detail_overlay" role="dialog" aria-modal="true" aria-labelledby="cp-detail-title">
          <button type="button" className="cp_detail_scrim" aria-label="Close" onClick={() => setDetailsModal(null)} />
          <div className="cp_detail_card">
            <div className="cp_detail_head">
              <h2 id="cp-detail-title" className="cp_detail_title">
                {detailsModal.name}
              </h2>
              <button type="button" className="cp_detail_close" onClick={() => setDetailsModal(null)}>
                ×
              </button>
            </div>
            <div className="cp_detail_body">
              <dl className="cp_detail_dl">
                {detailsModal.legalName && detailsModal.legalName !== detailsModal.name ? (
                  <>
                    <dt>Legal name</dt>
                    <dd>{detailsModal.legalName}</dd>
                  </>
                ) : null}
                <dt>Procedure</dt>
                <dd>{detailsModal.procedure}</dd>
                <dt>Clinical details</dt>
                <dd>{detailsModal.symptomsFull || "—"}</dd>
                <dt>Age</dt>
                <dd>{detailsModal.ageLabel}</dd>
                <dt>Blood group</dt>
                <dd>{detailsModal.bloodLabel}</dd>
                {detailsModal.gender ? (
                  <>
                    <dt>Gender</dt>
                    <dd>{detailsModal.gender}</dd>
                  </>
                ) : null}
                <dt>Location</dt>
                <dd>{detailsModal.location}</dd>
                <dt>Visit</dt>
                <dd>{detailsModal.appointmentDate}</dd>
              </dl>

              <div className="cp_detail_docs">
                <h3 className="cp_detail_docs_title">Documents for this request</h3>
                {docPreviewLoading ? <p className="cp_detail_muted">Loading previews…</p> : null}
                {!docPreviewLoading &&
                (!Array.isArray(detailsModal.raw?.prescriptionsAndReports) ||
                  detailsModal.raw.prescriptionsAndReports.length === 0) ? (
                  <p className="cp_detail_muted">No prescriptions or reports uploaded for this patient.</p>
                ) : null}
                <ul className="cp_detail_doc_list">
                  {(detailsModal.raw?.prescriptionsAndReports || []).map((doc) => (
                    <li key={String(doc._id)}>
                      <span className="cp_detail_doc_name">{doc.originalName || doc.type || "Document"}</span>
                      <span className="cp_detail_doc_meta">{String(doc.type || "").toLowerCase()}</span>
                      <button
                        type="button"
                        className="cp_detail_view_btn"
                        disabled={!previewDocUrl(doc)}
                        onClick={() => openFileViewer(doc, doc.originalName)}
                      >
                        View
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {fileViewer?.url ? (
        <div className="cp_file_overlay" role="dialog" aria-modal="true">
          <button type="button" className="cp_detail_scrim" aria-label="Close" onClick={() => setFileViewer(null)} />
          <div className="cp_file_card">
            <div className="cp_file_head">
              <h3 className="cp_file_title">{fileViewer.title}</h3>
              <button type="button" className="cp_detail_close" onClick={() => setFileViewer(null)}>
                ×
              </button>
            </div>
            <div className="cp_file_body">
              {fileViewer.fileType === "application/pdf" || fileViewer.title?.toLowerCase().endsWith(".pdf") ? (
                <iframe title={fileViewer.title} src={fileViewer.url} className="cp_file_frame" />
              ) : (
                <img src={fileViewer.url} alt={fileViewer.title} className="cp_file_img" />
              )}
            </div>
          </div>
        </div>
      ) : null}

      {chatModal?.bidId && (
        <BidHistoryChat
          open={true}
          onClose={() => setChatModal(null)}
          mode={chatMode}
          bidId={chatModal.bidId}
          patientName={chatModal.patientName}
          peerAvatarUrl={chatModal.raw?.patient?.avatar}
          initialBid={
            chatModal.raw
              ? {
                  affordabilityPrice: chatModal.raw.affordabilityPrice ?? chatModal.raw.bid,
                  providerAction: chatModal.raw.providerAction,
                  reverseBidAmount: chatModal.raw.reverseBidAmount,
                  patientAction: chatModal.raw.patientAction,
                  patientCounterAmount: chatModal.raw.patientCounterAmount,
                }
              : undefined
          }
          onBidUpdated={fetchConfirmed}
        />
      )}
    </DoctorLayout>
  );
}

function renderPages(page, totalPages, setPage) {
  const btn = (p) => (
    <button
      key={p}
      type="button"
      className={`confirmed_patient_pg_btn ${page === p ? "active" : ""}`}
      onClick={() => setPage(p)}
    >
      {p}
    </button>
  );

  if (totalPages <= 7) {
    return Array.from({ length: totalPages }).map((_, i) => btn(i + 1));
  }

  const items = [];
  items.push(btn(1));
  if (page > 3) {
    items.push(
      <span key="l1" className="confirmed_patient_dots">
        …
      </span>
    );
  }

  const start = Math.max(2, page - 1);
  const end = Math.min(totalPages - 1, page + 1);

  for (let p = start; p <= end; p++) {
    items.push(btn(p));
  }

  if (page < totalPages - 2) {
    items.push(
      <span key="r1" className="confirmed_patient_dots">
        …
      </span>
    );
  }

  items.push(btn(totalPages));
  return items;
}
